import { ReactNode } from 'react';
import {
  Drawer,
  DrawerContent,
} from './drawer';
import { DrawerCardHeader } from './drawer-card-header';

/**
 * Opinionated drawer-card primitive.
 *
 * Bundles `Drawer` + `DrawerContent` + the iOS scroll-edge-blur header
 * (`DrawerCardHeader`) + a scroll container, so callers don't have to
 * re-wire the same chrome on every sheet. Use it for any modal sheet that
 * should look like a native iOS card with the floating glass header.
 *
 * Children render below the header and scroll *under* it with the same
 * feathered edge-blur the main app header uses.
 *
 * For a sheet that hosts a `<form>` (e.g. New Task), pass `formId` —
 * the `<form>` becomes the scroll container so a header `Save` pill with
 * `<GlassPill type="submit" form={formId}>` can submit it from outside.
 *
 * @example
 * <DrawerCard
 *   open={showNewTask}
 *   onOpenChange={setShowNewTask}
 *   title="New Task"
 *   leading={<GlassPill icon={X} ariaLabel="Cancel" onClick={...} />}
 *   trailing={<GlassPill icon={Check} ariaLabel="Save" type="submit" form="new-task-form" />}
 *   formId="new-task-form"
 *   onSubmit={handleSubmit}
 * >
 *   ...form sections...
 * </DrawerCard>
 */
export interface DrawerCardProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  /** Hidden description for screen readers (Radix Dialog requires one). */
  description?: ReactNode;
  /** Glass pill rendered on the left of the header (typically Cancel/Back). */
  leading?: ReactNode;
  /** Glass pill rendered on the right of the header (typically Save/Done). */
  trailing?: ReactNode;
  /** Drawer body content. Renders inside a scroll container under the header. */
  children?: ReactNode;
  /**
   * Snap points (Vaul). Omit for a simple drawer with no snap behaviour
   * (drawer opens to content height, drag-to-dismiss via handle only).
   * Pass an array to enable mid-snap detents — e.g. `[0.5, 0.92]`.
   */
  snapPoints?: (number | string)[];
  /** Header band height. Default `96` — tall enough that the title row sits
   *  inside the full-strength blur zone (the ScrollEdgeBlur feather occupies
   *  the lower half), so scrolled rows can't ghost legibly at title height. */
  headerHeight?: number;
  /**
   * If supplied, the scroll container becomes a `<form id={formId}>` with
   * `onSubmit={onSubmit}`. The header's `trailing` `GlassPill` can then
   * `type="submit" form={formId}` to submit from outside the form tag.
   */
  formId?: string;
  onSubmit?: (event: React.FormEvent<HTMLFormElement>) => void;
  /** Extra padding/spacing classes for the scroll container's inner pad area. */
  bodyClassName?: string;
}

export function DrawerCard({
  open,
  onOpenChange,
  title,
  description,
  leading,
  trailing,
  children,
  snapPoints,
  headerHeight = 96,
  formId,
  onSubmit,
  bodyClassName = 'px-5 pb-6 space-y-8',
}: DrawerCardProps) {
  // The header OVERLAYS the scroll container (absolute band; pointer-events
  // none except the pills) so rows visually slide under the feathered
  // scroll-edge blur — the same iOS effect as the main app header. NOTE:
  // sticky-inside-scroll is still avoided (Vaul + backdrop-filter blocked
  // touch propagation there); an absolute sibling overlay doesn't have that
  // problem because it never participates in the scroll container's touch
  // handling. Content starts below the band via paddingTop on the body.
  const scrollWrapperClass = 'flex-1 min-h-0 overflow-y-auto overscroll-contain';

  const drawerProps = snapPoints
    ? {
        snapPoints,
        activeSnapPoint: snapPoints[snapPoints.length - 1],
        setActiveSnapPoint: () => {},
      }
    : {};

  const header = (
    <DrawerCardHeader
      title={title}
      description={description}
      leading={leading}
      trailing={trailing}
      height={headerHeight}
      position="absolute"
    />
  );

  const bodyStyle = { paddingTop: headerHeight };

  return (
    <Drawer
      open={open}
      onOpenChange={onOpenChange}
      {...drawerProps}
    >
      <DrawerContent className="rounded-t-[var(--radius-card)] border-glass-border flex flex-col">
        <div className="relative flex-1 min-h-0 flex flex-col">
          {header}
          {formId ? (
            <form id={formId} onSubmit={onSubmit} className={scrollWrapperClass}>
              <div className={bodyClassName} style={bodyStyle}>{children}</div>
            </form>
          ) : (
            <div className={scrollWrapperClass}>
              <div className={bodyClassName} style={bodyStyle}>{children}</div>
            </div>
          )}
        </div>
      </DrawerContent>
    </Drawer>
  );
}
